import {useEffect, useState} from 'react';

// Storage keys
const LIKED_ITEMS_KEY = 'liked_items';
const SEARCHES_KEY = 'searches';
const CURRENT_SEARCH_ID_KEY = 'current_search_id';
const TAGS_KEY = 'tags';

// Types
export type SearchFilters = {
  priceMin?: number;
  priceMax?: number;
  surfaceMin?: number;
  surfaceMax?: number;
  bedroomsMin?: number;
  bedroomsMax?: number;
  roomsMin?: number;
  roomsMax?: number;
  landSurfaceMin?: number;
  landSurfaceMax?: number;
  floorNbMin?: number;
  floorNbMax?: number;
  energyCertificateMin?: string;
  energyCertificateMax?: string;
  typeLabel?: string[];
  imagesMin?: number;
  imagesMax?: number;
};

export type SavedSearch = {
  id: string;
  name: string;
  filters: SearchFilters;
  lastUsed: number; // timestamp
  isDefault?: boolean;
};

// Liked Ids
export function getLikedIds(): Set<number> {
  try {
    const stored = localStorage.getItem(LIKED_ITEMS_KEY);
    if (!stored) return new Set();
    const ids = JSON.parse(stored) as number[];
    return new Set(ids);
  } catch {
    return new Set();
  }
}

export function setLikedIds(ids: Set<number>): void {
  localStorage.setItem(LIKED_ITEMS_KEY, JSON.stringify(Array.from(ids)));
  // Dispatch custom event for same-tab updates
  window.dispatchEvent(new CustomEvent('likedIdsChanged', {detail: {key: LIKED_ITEMS_KEY}}));
}

export function toggleLikedItem(itemId: number): boolean {
  const liked = getLikedIds();
  const isLiked = liked.has(itemId);
  if (isLiked) {
    liked.delete(itemId);
  } else {
    liked.add(itemId);
  }
  setLikedIds(liked);
  return !isLiked; // returns new state (true = now liked)
}

export function isItemLiked(itemId: number): boolean {
  return getLikedIds().has(itemId);
}

export function useLikedIds(): number[] {
  const [likedIds, setLikedIds] = useState<number[]>(() => {
    return Array.from(getLikedIds());
  });

  useEffect(() => {
    // Update state when localStorage changes
    const updateLikedIds = () => {
      setLikedIds(Array.from(getLikedIds()));
    };

    // Listen for storage events (changes from other tabs/windows)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LIKED_ITEMS_KEY) {
        updateLikedIds();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom events (changes in the same tab)
    const handleCustomStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.key === LIKED_ITEMS_KEY) {
        updateLikedIds();
      }
    };
    window.addEventListener('likedIdsChanged', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('likedIdsChanged', handleCustomStorageChange);
    };
  }, []);

  return likedIds;
}

function getDefaultSearch(): SavedSearch {
  return {
    id: 'default',
    name: 'Recherche par défaut',
    filters: {},
    lastUsed: Date.now(),
    isDefault: true,
  };
}

// Searches
export function getSearches(): SavedSearch[] {
  try {
    const stored = localStorage.getItem(SEARCHES_KEY);
    if (!stored) {
      // Initialize with default search
      const defaultSearch = getDefaultSearch();
      setSearches([defaultSearch]);
      return [defaultSearch];
    }
    return JSON.parse(stored) as SavedSearch[];
  } catch {
    const defaultSearch = getDefaultSearch();
    setSearches([defaultSearch]);
    return [defaultSearch];
  }
}

export function setSearches(searches: SavedSearch[]): void {
  localStorage.setItem(SEARCHES_KEY, JSON.stringify(searches));
}

export function getCurrentSearchId(): string {
  const stored = localStorage.getItem(CURRENT_SEARCH_ID_KEY);
  if (stored) return stored;
  // Return default search ID if exists
  const searches = getSearches();
  const defaultSearch = searches.find(s => s.isDefault);
  return defaultSearch?.id ?? 'default';
}

export function setCurrentSearchId(id: string): void {
  localStorage.setItem(CURRENT_SEARCH_ID_KEY, id);
  // Update lastUsed timestamp
  const searches = getSearches();
  const search = searches.find(s => s.id === id);
  if (search) {
    search.lastUsed = Date.now();
    setSearches(searches);
  }
}

export function getCurrentSearch(): SavedSearch | undefined {
  const id = getCurrentSearchId();
  const searches = getSearches();
  return searches.find(s => s.id === id);
}

export function saveSearch(search: SavedSearch): void {
  const searches = getSearches();
  const existingIndex = searches.findIndex(s => s.id === search.id);
  if (existingIndex >= 0) {
    searches[existingIndex] = search;
  } else {
    searches.push(search);
  }
  setSearches(searches);
}

export function deleteSearch(id: string): void {
  const searches = getSearches();
  const filtered = searches.filter(s => s.id !== id);
  // Don't allow deleting the default search
  if (filtered.length === 0) {
    const defaultSearch: SavedSearch = {
      id: 'default',
      name: 'Recherche par défaut',
      filters: {},
      lastUsed: Date.now(),
      isDefault: true,
    };
    setSearches([defaultSearch]);
  } else {
    setSearches(filtered);
  }
  // If deleted search was current, switch to default
  if (getCurrentSearchId() === id) {
    const defaultSearch = filtered.find(s => s.isDefault) ?? filtered[0];
    if (!defaultSearch) {
      console.error('No default search found after deleting a search');
      return;
    }
    setCurrentSearchId(defaultSearch.id);
  }
}

export function createNewSearch(name: string, filters: SearchFilters): SavedSearch {
  const newSearch: SavedSearch = {
    id: `search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name,
    filters,
    lastUsed: Date.now(),
  };
  saveSearch(newSearch);
  return newSearch;
}

// Tags
export type ItemTags = Record<number, string[]>; // itemId -> tags[]

export function getTagsData(): ItemTags {
  try {
    const stored = localStorage.getItem(TAGS_KEY);
    if (!stored) return {};
    return JSON.parse(stored) as ItemTags;
  } catch {
    return {};
  }
}

export function setTagsData(tags: ItemTags): void {
  localStorage.setItem(TAGS_KEY, JSON.stringify(tags));
  // Dispatch custom event for same-tab updates
  window.dispatchEvent(new CustomEvent('tagsChanged', {detail: {key: TAGS_KEY}}));
}

export function getItemTags(itemId: number): string[] {
  const tags = getTagsData();
  return tags[itemId] ?? [];
}

export function setItemTags(itemId: number, tagList: string[]): void {
  const tags = getTagsData();
  if (tagList.length === 0) {
    delete tags[itemId];
  } else {
    tags[itemId] = tagList;
  }
  setTagsData(tags);
}

export function addTagToItem(itemId: number, tag: string): void {
  const currentTags = getItemTags(itemId);
  if (!currentTags.includes(tag)) {
    setItemTags(itemId, [...currentTags, tag]);
  }
}

export function removeTagFromItem(itemId: number, tag: string): void {
  const currentTags = getItemTags(itemId);
  setItemTags(
    itemId,
    currentTags.filter(t => t !== tag)
  );
}

export function getAllTags(): string[] {
  const tags = getTagsData();
  const allTags = new Set<string>();
  for (const tagList of Object.values(tags)) {
    for (const tag of tagList) {
      allTags.add(tag);
    }
  }
  return Array.from(allTags).sort();
}

export function useTagsData(): ItemTags {
  const [tagsData, setTagsData] = useState<ItemTags>(() => {
    return getTagsData();
  });

  useEffect(() => {
    // Update state when localStorage changes
    const updateTags = () => {
      setTagsData(getTagsData());
    };

    // Listen for storage events (changes from other tabs/windows)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === TAGS_KEY) {
        updateTags();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom events (changes in the same tab)
    const handleCustomStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.key === TAGS_KEY) {
        updateTags();
      }
    };
    window.addEventListener('tagsChanged', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tagsChanged', handleCustomStorageChange);
    };
  }, []);

  return tagsData;
}

export function useAllTags(): string[] {
  const tagsData = useTagsData();
  const [allTags, setAllTags] = useState<string[]>(() => {
    return getAllTags();
  });

  useEffect(() => {
    const allTagsSet = new Set<string>();
    for (const tagList of Object.values(tagsData)) {
      for (const tag of tagList) {
        allTagsSet.add(tag);
      }
    }
    setAllTags(Array.from(allTagsSet).sort());
  }, [tagsData]);

  return allTags;
}
